/**
 * "Remember what people tell it" (ADR-0085): the Builder's plain memory choice and the
 * `spec.memory` it maps to.
 *
 * - Off: `{ scopes: [] }` (the guided builder's default; nothing is kept or recalled).
 * - On: `semantic: true` with one scope. `agent`: only this agent, per person. `user`: the
 *   person's own memory, which their other agents that remember may also use.
 *
 * Either way a person is only remembered after they allow it (the Assistant's memory switch),
 * and they can see and forget it at any time ("What agents remember about me").
 */
import type { AgentSpec } from "./types";

export type SpecMemory = NonNullable<AgentSpec["spec"]["memory"]>;
export type MemoryReach = "agent" | "user";

export interface MemoryChoice {
  on: boolean;
  reach: MemoryReach;
  /** How long it keeps what it was told, in days. */
  retentionDays: number;
}

export const MEMORY_REACHES: readonly MemoryReach[] = ["agent", "user"];
export const MEMORY_RETENTION_DAYS: readonly number[] = [7, 30, 90, 365];
export const MEMORY_OFF: MemoryChoice = { on: false, reach: "agent", retentionDays: 30 };

/** The plain choice a spec's memory section stands for. */
export function memoryChoice(mem: SpecMemory | undefined | null): MemoryChoice {
  const scopes = mem?.scopes ?? [];
  const reach: MemoryReach = scopes.includes("user") ? "user" : "agent";
  const on = !!mem?.semantic && (scopes.includes("user") || scopes.includes("agent"));
  const days = mem?.retention_days;
  return { on, reach, retentionDays: typeof days === "number" && days > 0 ? days : MEMORY_OFF.retentionDays };
}

/** The spec memory section for a choice; `base` keeps anything else an advanced editor set. */
export function memorySpec(choice: MemoryChoice | undefined, base?: SpecMemory | null): SpecMemory {
  if (!choice?.on) {
    if (!base) return { scopes: [] };
    // Turning it off stops keeping and recalling; other settings stay as they were.
    return { ...base, scopes: (base.scopes ?? []).filter((s) => s !== "user" && s !== "agent"), semantic: false };
  }
  const others = (base?.scopes ?? []).filter((s) => s !== "user" && s !== "agent");
  return { ...(base ?? {}), scopes: [...others, choice.reach], semantic: true, retention_days: choice.retentionDays };
}

/** The guided builder's state field: absent (or off) unless the builder turned it on. */
export function wizardMemoryFromSpec(mem: SpecMemory | undefined | null): { memory?: MemoryChoice } {
  const choice = memoryChoice(mem);
  return choice.on ? { memory: choice } : {};
}
